/**
 * 🛡️ G2GENTE - ARQUIVO PROTEGIDO | SELO: G2GENTE-PROTECTED-2026-02-24-V1
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { candidatesAPI, lgpdAPI, authAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft,
  Loader2,
  Shield,
  Bell,
  Eye,
  Lock,
  Trash2,
  LogOut,
  ChevronRight,
  Moon,
  Globe,
  Download,
  FileText,
  AlertTriangle,
  KeyRound,
  X,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

export default function MobileSettings() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [consents, setConsents] = useState({});
  const [exporting, setExporting] = useState(false);
  const [deletionConfirm, setDeletionConfirm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [settings, setSettings] = useState({
    consent_share: true,
    notifications: true,
    darkMode: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, consentsRes] = await Promise.all([
        candidatesAPI.getMe(),
        lgpdAPI.getConsents().catch(() => ({ data: { consents: {} } }))
      ]);
      setProfile(profileRes.data);
      setConsents(consentsRes.data.consents || {});
      setSettings(s => ({ ...s, consent_share: profileRes.data.consent_share }));
    } catch (error) {
      console.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleConsentChange = async (value) => {
    try {
      await candidatesAPI.updateMe({ consent_share: value });
      setSettings(s => ({ ...s, consent_share: value }));
      toast.success(value ? 'Perfil visível na busca' : 'Perfil oculto da busca');
    } catch (error) {
      toast.error('Erro ao atualizar configuração');
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    setChangingPassword(true);
    try {
      await authAPI.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      });
      toast.success('Senha alterada com sucesso!');
      setShowPasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Senha atual incorreta');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLGPDConsentChange = async (consentType, value) => {
    try {
      await lgpdAPI.updateConsent(consentType, value);
      setConsents(c => ({ ...c, [consentType]: value }));
      toast.success('Preferência atualizada');
    } catch (error) {
      toast.error('Erro ao atualizar preferência');
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      const response = await lgpdAPI.exportData();
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `g2egente-meus-dados-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Dados exportados com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar dados');
    } finally {
      setExporting(false);
    }
  };

  const handleDeletionRequest = async () => {
    try {
      await lgpdAPI.requestDeletion();
      toast.success('Solicitação de exclusão enviada. Você será notificado.');
      setDeletionConfirm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar exclusão');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/m/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="mobile-settings">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 border-b border-slate-200">
        <div className="flex items-center gap-3 p-4">
          <button onClick={() => navigate('/m')} className="text-slate-600">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-bold text-slate-900">Configurações</h1>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Privacy Section */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-cyan-600" />
                <h3 className="font-semibold text-slate-900">Privacidade</h3>
              </div>
            </div>
            
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Visível na Busca</p>
                  <p className="text-xs text-slate-500">Seu perfil aparece para as empresas</p>
                </div>
              </div>
              <span className="text-xs font-medium text-green-700" data-testid="mobile-settings-always-visible">Sempre ativo</span>
            </div>
            
            <Separator />
            
            <button 
              className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
              onClick={() => toast.info('Funcionalidade em desenvolvimento')}
            >
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-slate-400" />
                <div className="text-left">
                  <p className="font-medium text-slate-900">Dados Protegidos</p>
                  <p className="text-xs text-slate-500">Ver o que empresas podem acessar</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
          </CardContent>
        </Card>

        {/* Notifications Section */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-cyan-600" />
                <h3 className="font-semibold text-slate-900">Notificações</h3>
              </div>
            </div>
            
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Push Notifications</p>
                  <p className="text-xs text-slate-500">Receber alertas de vagas e mensagens</p>
                </div>
              </div>
              <Switch
                checked={settings.notifications}
                onCheckedChange={(v) => {
                  setSettings(s => ({ ...s, notifications: v }));
                  toast.success(v ? 'Notificações ativadas' : 'Notificações desativadas');
                }}
              />
            </div>
          </CardContent>
        </Card>

        {/* App Section */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-cyan-600" />
                <h3 className="font-semibold text-slate-900">Conta</h3>
              </div>
            </div>
            
            <button 
              className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
              onClick={() => navigate('/m/profile/edit')}
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-400" />
                <div className="text-left">
                  <p className="font-medium text-slate-900">Editar Dados Pessoais</p>
                  <p className="text-xs text-slate-500">Atualizar seu perfil</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
            
            <Separator />
            
            <button 
              className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
              onClick={() => setShowPasswordModal(true)}
            >
              <div className="flex items-center gap-3">
                <KeyRound className="w-5 h-5 text-slate-400" />
                <div className="text-left">
                  <p className="font-medium text-slate-900">Alterar Senha</p>
                  <p className="text-xs text-slate-500">Modificar sua senha de acesso</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </button>
            
            <Separator />
            
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Moon className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Modo Escuro</p>
                  <p className="text-xs text-slate-500">Em breve</p>
                </div>
              </div>
              <Switch
                checked={settings.darkMode}
                disabled
              />
            </div>
          </CardContent>
        </Card>

        {/* Info */}
        <Card className="border-cyan-200 bg-cyan-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-cyan-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-cyan-900 text-sm">Seus dados estão seguros</h3>
                <p className="text-xs text-cyan-700 mt-1">
                  Empresas só acessam seus dados completos após sua aprovação explícita. 
                  Informações de inclusão (PCD, 50+) são exibidas apenas como "Elegível a políticas de inclusão".
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* LGPD Section */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="p-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-cyan-600" />
                <h3 className="font-semibold text-slate-900">LGPD - Seus Direitos</h3>
              </div>
            </div>
            
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Marketing</p>
                  <p className="text-xs text-slate-500">Receber comunicações promocionais</p>
                </div>
              </div>
              <Switch
                checked={consents.marketing || false}
                onCheckedChange={(v) => handleLGPDConsentChange('marketing', v)}
              />
            </div>
            
            <Separator />
            
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-900">Compartilhamento</p>
                  <p className="text-xs text-slate-500">Compartilhar dados com parceiros</p>
                </div>
              </div>
              <Switch
                checked={consents.third_party_sharing || false}
                onCheckedChange={(v) => handleLGPDConsentChange('third_party_sharing', v)}
              />
            </div>
            
            <Separator />
            
            <button 
              className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
              onClick={handleExportData}
              disabled={exporting}
            >
              <div className="flex items-center gap-3">
                <Download className="w-5 h-5 text-slate-400" />
                <div className="text-left">
                  <p className="font-medium text-slate-900">Exportar Meus Dados</p>
                  <p className="text-xs text-slate-500">Baixar todos os seus dados (JSON)</p>
                </div>
              </div>
              {exporting ? (
                <Loader2 className="w-5 h-5 text-cyan-600 animate-spin" />
              ) : (
                <ChevronRight className="w-5 h-5 text-slate-400" />
              )}
            </button>
            
            <Separator />
            
            <a 
              href="/privacy-policy"
              target="_blank"
              className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-slate-400" />
                <div className="text-left">
                  <p className="font-medium text-slate-900">Política de Privacidade</p>
                  <p className="text-xs text-slate-500">Ler termos de uso e privacidade</p>
                </div>
              </div>
              <ExternalLink className="w-5 h-5 text-slate-400" />
            </a>
          </CardContent>
        </Card>

        {/* Logout & Delete */}
        <div className="space-y-3 pt-4">
          <Button 
            variant="outline"
            className="w-full justify-start text-slate-700"
            onClick={handleLogout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sair da Conta
          </Button>
          
          {!deletionConfirm ? (
            <Button 
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setDeletionConfirm(true)}
            >
              <Trash2 className="w-5 h-5 mr-3" />
              Excluir Conta e Dados
            </Button>
          ) : (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-medium text-red-900">Tem certeza?</p>
                    <p className="text-xs text-red-700">Esta ação não pode ser desfeita. Todos os seus dados serão excluídos permanentemente.</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setDeletionConfirm(false)}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={handleDeletionRequest}
                    className="flex-1"
                  >
                    Confirmar Exclusão
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Version */}
        <div className="text-center pt-6">
          <p className="text-xs text-slate-400">G2eGente v1.0.0</p>
          <p className="text-xs text-slate-400">© 2024 G2 Consultoria RH</p>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-sm border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Alterar Senha</h3>
                <button 
                  onClick={() => setShowPasswordModal(false)}
                  className="text-slate-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-slate-500">Senha Atual</Label>
                  <Input
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({
                      ...passwordData, 
                      currentPassword: e.target.value
                    })}
                    placeholder="Digite sua senha atual"
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-slate-500">Nova Senha</Label>
                  <Input
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({
                      ...passwordData, 
                      newPassword: e.target.value
                    })}
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
                
                <div>
                  <Label className="text-xs text-slate-500">Confirmar Nova Senha</Label>
                  <Input
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({
                      ...passwordData, 
                      confirmPassword: e.target.value
                    })}
                    placeholder="Digite novamente"
                  />
                </div>
                
                <Button
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                >
                  {changingPassword ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Salvar Nova Senha'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
